import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useIntercom } from "react-use-intercom";

import { useDeals, useStakeDebit } from "../../CustomHooks/Queries";

import {
  getBankAccountMigrationStatusFromDealsResponse,
  getBankMigrationSkippedStatusKey,
  getBankMigrationTrackerVariantFromMigratingBankStatus,
  getMigratingBankStatusFromStakeDebitResponse,
  isBankAccountMigrationNotRequiredByDealsResponse,
  shouldShowMigrationAcknowledgementFromStakeDebitResponse,
  BANK_MIGRATION_TRACKER_STATUS,
  LocalStorage,
} from "../../Utils/helpers";

import { Tick } from "../../Assets/Icons";

import "./bank-migration-status.scss";
import "./bank-migration-status-responsive.scss";

/**
 * Tracker variants aligned to Paper status screens:
 * - Application Pending: https://app.paper.design/file/01KN2CZMCH8A0647ESW3XZNRAX/01KN2CZMCJB0BND37NRC75ZP1V/126T-0
 * - Under Review: https://app.paper.design/file/01KN2CZMCH8A0647ESW3XZNRAX/01KN2CZMCJB0BND37NRC75ZP1V/TMA-0
 * - Verification required: https://app.paper.design/file/01KN2CZMCH8A0647ESW3XZNRAX/01KN2CZMCJB0BND37NRC75ZP1V/1222-0
 * - Verification rejected: https://app.paper.design/file/01KN2CZMCH8A0647ESW3XZNRAX/01KN2CZMCJB0BND37NRC75ZP1V/13C3-0
 */

const TRACKER_VARIANT_CONFIG = {
  [BANK_MIGRATION_TRACKER_STATUS.APPLICATION_PENDING]: {
    cardTitle: "Bank Migration",
    step3: {
      title: "Application Pending",
      description: "Your application is being processed. We'll notify you once there's an update.",
      icon: "active",
    },
  },
  [BANK_MIGRATION_TRACKER_STATUS.UNDER_REVIEW]: {
    cardTitle: "Bank Migration",
    step3: {
      title: "Under Review",
      description: "We're reviewing your application. We'll notify you once there's an update.",
      icon: "active",
    },
  },
  [BANK_MIGRATION_TRACKER_STATUS.VERIFICATION_REQUIRED]: {
    cardTitle: "Migration Status",
    step3: {
      title: "Verification Required",
      description: "We need a bit more information to verify your identity.",
      icon: "verification_required",
    },
  },
  [BANK_MIGRATION_TRACKER_STATUS.VERIFICATION_REJECTED]: {
    cardTitle: "Migration Status",
    step3: {
      title: "Verification Rejected",
      description: "We couldn't finish your migration. Tap here to contact support.",
      icon: "verification_rejected",
    },
  },
};

function StepIconForTrackerStep({ icon }) {
  if (icon === "verification_required" || icon === "verification_rejected") {
    return <StepIconVerificationRequiredPaper />;
  }

  return <StepIconActive />;
}

function StepIconComplete() {
  return (
    <div className="migration-status-step__icon migration-status-step__icon--complete">
      <span className="migration-status-step__tick" aria-hidden>
        <Tick />
      </span>
    </div>
  );
}

function StepIconActive() {
  return (
    <div className="migration-status-step__icon migration-status-step__icon--active">
      <span className="migration-status-step__dot" />
    </div>
  );
}

/** Paper 12MR-0: red ring + “!” (not the filled exclamation icon). */
function StepIconVerificationRequiredPaper() {
  return (
    <div className="migration-status-step__icon migration-status-step__icon--verification-paper" aria-hidden>
      <span className="migration-status-step__verification-bang">!</span>
    </div>
  );
}

function StepIconUpcoming() {
  return <div className="migration-status-step__icon migration-status-step__icon--upcoming" aria-hidden />;
}

function InlineCheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M4 9L7.5 12.5L14 5.5" stroke="#54FF99" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InlineClockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="9" cy="9" r="7.5" stroke="#C4B73B" strokeWidth="1.2" />
      <path d="M9 5.5V9.5L11.5 11" stroke="#C4B73B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MigrationStatusItem({ Icon, title, description }) {
  return (
    <div className="bank-migration-status__status-item">
      <span className="bank-migration-status__status-item-icon">
        <Icon />
      </span>
      <span className="bank-migration-status__status-item-copy">
        <span className="bank-migration-status__status-item-title">{title}</span>
        <span className="bank-migration-status__status-item-desc">{description}</span>
      </span>
    </div>
  );
}

function BankMigrationApprovedCard({ onDone }) {
  return (
    <div className="bank-migration-status__card bank-migration-status__card--approved">
      <h1 className="bank-migration-status__approved-title">Your account has been successfully migrated.</h1>
      <p className="bank-migration-status__approved-subtitle">
        Your Stake Checking account is now active. Your balance, transactions, and settings have been securely
        transferred.
      </p>
      <div className="bank-migration-status__divider" />

      <div className="bank-migration-status__status-group bank-migration-status__status-group--completed">
        <p className="bank-migration-status__status-label bank-migration-status__status-label--completed">Completed</p>
        <MigrationStatusItem
          Icon={InlineCheckIcon}
          title="Stake Checking Account"
          description="Your account is approved and active."
        />
        <MigrationStatusItem
          Icon={InlineCheckIcon}
          title="Balance, Transactions & Settings"
          description="Everything has been securely transferred."
        />
        <MigrationStatusItem
          Icon={InlineCheckIcon}
          title="Previous Debit Cards Closed"
          description="New cards will be issued soon."
        />
      </div>

      <div className="bank-migration-status__status-group bank-migration-status__status-group--processing">
        <p className="bank-migration-status__status-label bank-migration-status__status-label--processing">Processing</p>
        <MigrationStatusItem
          Icon={InlineClockIcon}
          title="Stake Visa Debit Card"
          description="We'll notify you as soon as your new card is ready."
        />
      </div>

      <button type="button" className="bank-migration-status__done-btn" onClick={onDone}>
        Done
      </button>
    </div>
  );
}

export default function BankMigrationStatus() {
  const navigate = useNavigate();
  const { show: showIntercom } = useIntercom();

  const { data: stakeDebit, isLoading: isLoadingStakeDebit } = useStakeDebit();
  const { data: dealsData, isLoading: isLoadingDeals } = useDeals();
  const isLoading = isLoadingStakeDebit || isLoadingDeals;

  const showMigrationAcknowledgement = shouldShowMigrationAcknowledgementFromStakeDebitResponse(stakeDebit);
  const stakeDebitStatus = stakeDebit?.data?.data?.status || stakeDebit?.data?.status;
  const isDepositAccountCreated = stakeDebitStatus === "DepositAccountCreated";
  const migrationCompleteByDeals = isBankAccountMigrationNotRequiredByDealsResponse(dealsData);
  const showApprovedMigrationCard = isDepositAccountCreated || migrationCompleteByDeals;

  const trackerVariant = useMemo(
    () =>
      getBankMigrationTrackerVariantFromMigratingBankStatus(
        getMigratingBankStatusFromStakeDebitResponse(stakeDebit),
      ),
    [stakeDebit],
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (showMigrationAcknowledgement) {
      navigate("/bank-migration-acknowledgement", { replace: true });
      return;
    }

    if (showApprovedMigrationCard || trackerVariant) {
      return;
    }

    navigate("/home", { replace: true });
  }, [isLoading, navigate, showApprovedMigrationCard, showMigrationAcknowledgement, trackerVariant]);

  const trackerConfig =
    TRACKER_VARIANT_CONFIG[trackerVariant] ??
    TRACKER_VARIANT_CONFIG[BANK_MIGRATION_TRACKER_STATUS.APPLICATION_PENDING];
  const step3 = trackerConfig.step3;
  const cardTitle = trackerConfig.cardTitle;

  const handleStartVerification = useCallback(() => {
    navigate("/verify-stake-checking", { state: { previousPathname: "/bank-migration-status" } });
  }, [navigate]);

  if (isLoading) {
    return (
      <section className="authenticated-page bank-migration-status">
        <div className="bank-migration-status max-width-container">
          <CircularProgress />
        </div>
      </section>
    );
  }

  return (
    <section className="authenticated-page bank-migration-status">
      <div className="bank-migration-status max-width-container">
        {showApprovedMigrationCard ? (
          <BankMigrationApprovedCard
            onDone={() => {
              const migrationStatus = getBankAccountMigrationStatusFromDealsResponse(dealsData);
              if (migrationStatus) {
                LocalStorage.write(getBankMigrationSkippedStatusKey(), migrationStatus);
              }
              navigate("/home", { replace: true });
            }}
          />
        ) : (
          <div className="bank-migration-status__card" key={trackerVariant}>
            <h1 className="bank-migration-status__title">{cardTitle}</h1>

            <ol className="bank-migration-status__steps">
              <li className="bank-migration-status__step">
                <StepIconComplete />
                <div className="bank-migration-status__step-body">
                  <p className="bank-migration-status__step-title">Migration Started</p>
                  <p className="bank-migration-status__step-desc">
                    You agreed to move your account to our new banking partner.
                  </p>
                </div>
              </li>

              <li className="bank-migration-status__step">
                <StepIconComplete />
                <div className="bank-migration-status__step-body">
                  <p className="bank-migration-status__step-title">Application Submitted</p>
                  <p className="bank-migration-status__step-desc">
                    We&apos;ve received your Stake Checking application.
                  </p>
                </div>
              </li>

              <li className="bank-migration-status__step">
                <StepIconForTrackerStep icon={step3.icon} />
                <div className="bank-migration-status__step-body">
                  <p className="bank-migration-status__step-title">{step3.title}</p>
                  <p className="bank-migration-status__step-desc">{step3.description}</p>
                  {trackerVariant === BANK_MIGRATION_TRACKER_STATUS.VERIFICATION_REQUIRED && (
                    <button
                      type="button"
                      className="bank-migration-status__start-verification"
                      onClick={handleStartVerification}
                    >
                      Start Verification ›
                    </button>
                  )}
                </div>
              </li>

              <li className="bank-migration-status__step bank-migration-status__step--muted">
                <StepIconUpcoming />
                <div className="bank-migration-status__step-body">
                  <p className="bank-migration-status__step-title">Application Approved</p>
                  <p className="bank-migration-status__step-desc">Your new account will be active once approved.</p>
                </div>
              </li>
            </ol>

            <div className="bank-migration-status__footer">
              <button type="button" className="bank-migration-status__support" onClick={showIntercom}>
                Need help? Contact Support &gt;
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
